/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback, useState } from 'preact/hooks';
import { isKeyDefined } from '../../utils/UNSAFE_keys';

/**
 * Hook for handling Tabbable mode set provider components.
 *
 * @param contains - callback to check  whether an element is in set
 * @param keyExtractor - function to extract the key based on provided element
 * @param currentKey - current key navigation "cursor" is on
 * @param onTabbableModeDisable - callback to parent when set is disabled
 * @param onTabbableModeEnable - callback to parent when set is enabled
 * @returns isCurrentTabbableKey - callback for parent to check if key is in interactive mode
 * @returns tabbableSetProps - event handlers mode relies on
 */
export function useTabbableModeSet<K>(
  contains: (elem: Element | null) => boolean,
  keyExtractor: (element: HTMLElement) => K | null,
  currentKey?: K,
  onTabbableModeDisable?: (target: EventTarget | null, isKeyboardEvent: boolean) => void,
  onTabbableModeEnable?: (key: K) => void
): [
  (key?: K) => boolean,
  {
    onFocus: (event: FocusEvent) => void;
    onBlur: (event: FocusEvent) => void;
    onKeyDown: (event: KeyboardEvent) => void;
    onPointerDown: (event: MouseEvent) => void;
  }
] {
  // Track which row is in actionable in state
  const [currentTabbableKey, setCurrentTabbableKey] = useState<K>();
  const disableTabbableMode = useCallback(
    (target: EventTarget | null, isKeyboardEvent = false) => {
      setCurrentTabbableKey(undefined);
      onTabbableModeDisable && onTabbableModeDisable(target, isKeyboardEvent);
    },
    [onTabbableModeDisable, setCurrentTabbableKey]
  );
  const enableTabbableMode = useCallback(
    (key: K) => {
      setCurrentTabbableKey(key);
      onTabbableModeEnable && onTabbableModeEnable(key);
    },
    [onTabbableModeEnable, setCurrentTabbableKey]
  );
  const isCurrentTabbableKey = useCallback(
    (key?: K) => {
      return key === currentTabbableKey;
    },
    [currentTabbableKey]
  );

  const onFocus = (event: FocusEvent) => {
    const key = keyExtractor(event.target as HTMLElement) as K;
    if (isKeyDefined(key) && !isCurrentTabbableKey(key)) {
      enableTabbableMode(key);
    }
  };

  const onBlur = (event: FocusEvent) => {
    if (!event.relatedTarget || !contains(event.relatedTarget as Element)) {
      disableTabbableMode(event.relatedTarget);
    }
  };

  const onPointerDown = (event: MouseEvent) => {
    const key = keyExtractor(event.target as HTMLElement) as K;
    if (currentTabbableKey !== undefined && !isCurrentTabbableKey(key)) {
      disableTabbableMode(event.target);
    }
  };

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'F2') {
        currentKey !== undefined && !isCurrentTabbableKey(currentKey)
          ? enableTabbableMode(currentKey)
          : disableTabbableMode(event.target, true);
      }
      if (event.key === 'Esc' || event.key === 'Escape') {
        disableTabbableMode(event.target, true);
      }
    },
    [currentKey, disableTabbableMode, enableTabbableMode, isCurrentTabbableKey]
  );

  const tabbableSetProps = {
    onFocus,
    onBlur,
    onKeyDown,
    onPointerDown
  };

  return [isCurrentTabbableKey, tabbableSetProps];
}
